import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import Button from '../../atoms/Button';
import TextField from '../../atoms/TextField';
import Modal, {
  MODAL_SIZE_LG,
  type ModalSize,
} from '../Modal';
import {
  CARD_PICKER_DENSITY_REGULAR,
  CARD_PICKER_SELECTION_SINGLE,
  type CardPickerDensity,
  type CardPickerSelectionMode,
} from './CardPickerModal.constants';
import {
  Card,
  CardBadgeSlot,
  CardDescription,
  CardFooter,
  CardHeaderRow,
  CardIcon,
  CardTitle,
  EmptyState,
  FooterLeft,
  FooterRight,
  FooterRow,
  Grid,
  Layout,
  ScrollArea,
  SearchRow,
  SelectionDot,
} from './CardPickerModal.styled';

export interface CardOption {
  /** Stable id used for keying + selection. */
  id: string;
  /** Card title — usually a string. */
  title: ReactNode;
  /**
   * Short description below the title. Stays under 2-3 lines for a
   * scannable grid.
   */
  description?: ReactNode;
  /** Hero icon — Material symbol, react-icons, raw svg. */
  icon?: ReactNode;
  /**
   * Inline badge in the card's header row (e.g. a `StatusPill`
   * "Recommended"). Pinned to the top-right.
   */
  badge?: ReactNode;
  /**
   * Optional footer node — typically a meta line ("Added 2 weeks ago",
   * "12 runs"). Pinned to the bottom of the card.
   */
  footer?: ReactNode;
  /** When true the card cannot be picked. */
  disabled?: boolean;
  /**
   * Searchable text — picked up by the built-in filter when
   * `searchable` is true. Defaults to `${title} ${description}`
   * stringified.
   */
  searchText?: string;
  /** Free-form data the consumer wants to attach (e.g. category). */
  meta?: Record<string, unknown>;
}

export interface CardPickerModalProps {
  /** Visibility — same contract as `Modal`. */
  open: boolean;
  /** Called when the user dismisses (Escape, backdrop, close, cancel). */
  onClose: () => void;
  /** Title shown in the modal header. */
  title: ReactNode;
  /** Optional subtitle under the title. */
  subtitle?: ReactNode;
  /** Optional 36×36 leading icon in the header — same as `Modal#icon`. */
  icon?: ReactNode;
  /** The grid of cards to choose from. */
  options: CardOption[];
  /**
   * Selection mode.
   *   - `single` (default): clicking a card sets the selection. By
   *     default the modal commits-and-closes (`commitOnSelect`); pass
   *     `commitOnSelect={false}` to require an explicit Confirm click.
   *   - `multiple`: cards toggle in/out; commit always requires the
   *     Confirm button.
   */
  selectionMode?: CardPickerSelectionMode;
  /**
   * Initial selection set — used as the seed when the modal opens. The
   * modal owns the selection state internally to keep the API simple;
   * the consumer gets the result via `onConfirm`.
   */
  initialSelected?: string[];
  /**
   * Called when the user confirms.
   *   - In `single` mode receives a single id (or `null` if cleared).
   *   - In `multiple` mode receives an array of ids.
   */
  onConfirm: (selected: string | null | string[]) => void;
  /**
   * In `single` mode, commit + close the modal as soon as a card is
   * picked (skipping the Confirm button). Defaults to `true`.
   */
  commitOnSelect?: boolean;
  /** Show a search input above the grid. Defaults to `false`. */
  searchable?: boolean;
  /** Placeholder for the search input. Defaults to "Search…". */
  searchPlaceholder?: string;
  /** Override the empty-state message shown when the filter zeroes out. */
  emptyState?: ReactNode;
  /** Grid density. Defaults to `regular`. */
  density?: CardPickerDensity;
  /** Modal size token. Defaults to `lg` (720px). */
  size?: ModalSize;
  /** Override the confirm button label (default depends on mode). */
  confirmLabel?: string;
  /** Override the cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Let the user confirm an empty selection, e.g. to clear a multi-select. Defaults to `false`. */
  allowEmpty?: boolean;
  /**
   * Render an optional helper line on the left side of the footer
   * (e.g. "3 of 24 selected"). When omitted in multiple mode, a default
   * "N selected" caption is shown automatically.
   */
  footerLeft?: ReactNode;
  className?: string;
  dataTestId?: string;
}

const CheckGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 12.5 10 17 19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const buildSearchKey = (option: CardOption): string => {
  if (option.searchText) return option.searchText.toLowerCase();
  // The title/description may be ReactNodes — fall back to the rough
  // string concatenation so basic substring matching still works for
  // the typical "string title + string description" shape.
  const titleStr = typeof option.title === 'string' ? option.title : '';
  const descStr =
    typeof option.description === 'string' ? option.description : '';
  return `${titleStr} ${descStr}`.toLowerCase();
};

/**
 * Modal with a grid of selectable cards — the universal "pick a
 * template / runner / preset" surface. Composes the {@link Modal}
 * organism for chrome, focus trap, and dismissal semantics, and adds
 * a responsive `auto-fill` grid + optional search filter + opinionated
 * footer with cancel/confirm.
 *
 * Selection state is owned **internally** so consumers don't have to
 * mirror it. Use `initialSelected` to seed and `onConfirm` to receive
 * the result.
 *
 * **Single-select** mode commits-and-closes on click by default
 * (`commitOnSelect`) — the most common UX for "pick one and go". Set
 * `commitOnSelect={false}` if your single-pick flow needs a confirm
 * step (e.g. shows a preview that the user should review first).
 *
 * **Multi-select** mode toggles cards on click and always requires the
 * explicit Confirm button.
 *
 * @example
 *   <CardPickerModal
 *     open={open}
 *     onClose={close}
 *     title="Choose a template"
 *     options={templates}
 *     onConfirm={(id) => createFromTemplate(id as string)}
 *   />
 *
 *   <CardPickerModal
 *     open={open}
 *     onClose={close}
 *     title="Pick runners to update"
 *     selectionMode="multiple"
 *     initialSelected={['r1', 'r3']}
 *     options={runners}
 *     searchable
 *     onConfirm={(ids) => updateRunners(ids as string[])}
 *   />
 */
const CardPickerModal: React.FC<CardPickerModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  options,
  selectionMode = CARD_PICKER_SELECTION_SINGLE,
  initialSelected,
  onConfirm,
  commitOnSelect = true,
  searchable = false,
  searchPlaceholder = 'Search…',
  emptyState,
  density = CARD_PICKER_DENSITY_REGULAR,
  size = MODAL_SIZE_LG,
  confirmLabel,
  cancelLabel = 'Cancel',
  allowEmpty = false,
  footerLeft,
  className,
  dataTestId = 'card-picker-modal',
}) => {
  const isMulti = selectionMode === 'multiple';
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelected ?? []),
  );
  const [query, setQuery] = useState('');

  // Reset internal state every time the modal re-opens so a previous
  // session's draft selection / search query doesn't leak into the
  // next open. (Don't reset on `open=true → false` because the close
  // animation could briefly flash the cleared state.)
  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelected ?? []));
      setQuery('');
    }
    // initialSelected is intentionally not in the dep list — we only
    // re-seed on the open transition so callers can update it freely
    // while the modal is closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((opt) => buildSearchKey(opt).includes(q));
  }, [options, searchable, query]);

  const handleCardClick = useCallback(
    (id: string) => {
      if (isMulti) {
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        return;
      }
      // Single mode.
      if (commitOnSelect) {
        onConfirm(id);
        onClose();
      } else {
        setSelected(new Set([id]));
      }
    },
    [isMulti, commitOnSelect, onClose, onConfirm],
  );

  const handleConfirm = useCallback(() => {
    if (isMulti) {
      onConfirm(Array.from(selected));
    } else {
      const first = selected.values().next().value;
      onConfirm(first ?? null);
    }
    onClose();
  }, [isMulti, onConfirm, onClose, selected]);

  const resolvedConfirmLabel =
    confirmLabel ?? (isMulti ? 'Confirm' : 'Select');
  const confirmDisabled = !allowEmpty && selected.size === 0;

  // The footer is only shown when the user needs an explicit Confirm
  // — i.e. in multi-select, or in single-select with commitOnSelect=false.
  const showFooter = isMulti || !commitOnSelect;

  const resolvedFooterLeft = (() => {
    if (footerLeft !== undefined) return footerLeft;
    if (isMulti) {
      return `${selected.size} selected`;
    }
    return null;
  })();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      icon={icon}
      size={size}
      className={className}
      dataTestId={dataTestId}
      footer={
        showFooter ? (
          <FooterRow data-testid={`${dataTestId}__footer`}>
            <FooterLeft data-testid={`${dataTestId}__footer-left`}>
              {resolvedFooterLeft}
            </FooterLeft>
            <FooterRight>
              <Button
                variant="text"
                onClick={onClose}
                dataTestId={`${dataTestId}__cancel`}
              >
                {cancelLabel}
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={confirmDisabled}
                dataTestId={`${dataTestId}__confirm`}
              >
                {resolvedConfirmLabel}
              </Button>
            </FooterRight>
          </FooterRow>
        ) : undefined
      }
    >
      <Layout data-testid={`${dataTestId}__layout`}>
        {searchable && (
          <SearchRow>
            <TextField
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder}
              stretch
              dataTestId={`${dataTestId}__search`}
              aria-label={searchPlaceholder}
            />
          </SearchRow>
        )}
        <ScrollArea data-testid={`${dataTestId}__scroll`}>
          {filtered.length === 0 ? (
            <EmptyState data-testid={`${dataTestId}__empty`}>
              {emptyState ?? (query ? 'No matches' : 'Nothing to choose from')}
            </EmptyState>
          ) : (
            <Grid
              $density={density}
              role={isMulti ? 'group' : 'radiogroup'}
              data-testid={`${dataTestId}__grid`}
            >
              {filtered.map((opt) => {
                const isSelected = selected.has(opt.id);
                return (
                  <Card
                    key={opt.id}
                    type="button"
                    $density={density}
                    $selected={isSelected}
                    $disabled={!!opt.disabled}
                    disabled={opt.disabled}
                    role={isMulti ? 'checkbox' : 'radio'}
                    aria-checked={isSelected}
                    data-card-id={opt.id}
                    data-testid={`${dataTestId}__card`}
                    onClick={() => handleCardClick(opt.id)}
                  >
                    <CardHeaderRow>
                      {opt.icon && <CardIcon>{opt.icon}</CardIcon>}
                      <SelectionDot $selected={isSelected} aria-hidden="true">
                        {isSelected && <CheckGlyph />}
                      </SelectionDot>
                      {opt.badge && <CardBadgeSlot>{opt.badge}</CardBadgeSlot>}
                    </CardHeaderRow>
                    <CardTitle data-testid={`${dataTestId}__card-title`}>
                      {opt.title}
                    </CardTitle>
                    {opt.description !== undefined &&
                      opt.description !== null && (
                        <CardDescription
                          data-testid={`${dataTestId}__card-description`}
                        >
                          {opt.description}
                        </CardDescription>
                      )}
                    {opt.footer !== undefined && opt.footer !== null && (
                      <CardFooter
                        data-testid={`${dataTestId}__card-footer`}
                      >
                        {opt.footer}
                      </CardFooter>
                    )}
                  </Card>
                );
              })}
            </Grid>
          )}
        </ScrollArea>
      </Layout>
    </Modal>
  );
};

export default CardPickerModal;
